import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import TEAMS from "../data/teams";

const C = {
  surf:"#161b22", card:"#1c2330", bdr:"#30363d",
  txt:"#e6edf3", muted:"#7d8590",
  blue:"#388bfd", grn:"#3fb950", red:"#f85149", ylw:"#e3b341",
};

const RESET_PASSWORD = "newseason1";

function pct(r) {
  const total = r.W + r.L;
  if (total === 0) return "—";
  const v = (r.W / total).toFixed(3);
  return v.startsWith("1") ? "1.000" : v.replace(/^0/, "");
}

export default function Standings({ isAdmin }) {
  const [records,   setRecords]   = useState({});
  const [loading,   setLoading]   = useState(true);
  const [resetStep, setResetStep] = useState("idle");
  const [pwInput,   setPwInput]   = useState("");
  const [pwError,   setPwError]   = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  useEffect(() => { fetchStandings(); }, []);

  async function fetchStandings() {
    setLoading(true);
    try {
      const ref  = doc(db, "league", "standings");
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setRecords(snap.data());
      } else {
        const empty = {};
        TEAMS.forEach(t => { empty[t.id] = { W:0, L:0 }; });
        setRecords(empty);
      }
    } catch (err) {
      console.error("Error fetching standings:", err);
    }
    setLoading(false);
  }

  function cancelReset() {
    setResetStep("idle");
    setPwInput("");
    setPwError("");
  }

  function checkPassword() {
    if (pwInput === RESET_PASSWORD) {
      setPwError("");
      setResetStep("confirm");
    } else {
      setPwError("Incorrect password. Try again.");
      setPwInput("");
    }
  }

  async function resetSeason() {
    setResetting(true);
    try {
      const freshStandings = {};
      TEAMS.forEach(t => { freshStandings[t.id] = { W:0, L:0 }; });
      await setDoc(doc(db, "league", "standings"), freshStandings);
      await deleteDoc(doc(db, "league", "playerStats"));
      setRecords(freshStandings);
      setResetStep("idle");
      setPwInput("");
      setResetDone(true);
      setTimeout(() => setResetDone(false), 3000);
    } catch (err) {
      console.error("Error resetting season:", err);
      alert("Error resetting. Please try again.");
    }
    setResetting(false);
  }

  const sorted = [...TEAMS].sort((a, b) => {
    const ra = records[a.id] || { W:0, L:0 };
    const rb = records[b.id] || { W:0, L:0 };
    if (rb.W !== ra.W) return rb.W - ra.W;
    return ra.L - rb.L;
  });

  const totalGames = Object.values(records).reduce((sum, r) => sum + (r.W || 0), 0);

  if (loading) return (
    <p style={{ color:C.muted, textAlign:"center", padding:40 }}>Loading standings...</p>
  );

  return (
    <div style={{ padding:"20px 14px" }}>
      <p style={{ color:C.muted, fontSize:11, fontWeight:700, letterSpacing:"1px", margin:"0 0 12px 2px" }}>
        STANDINGS
      </p>

      <div style={{ background:C.surf, borderRadius:12, border:`1px solid ${C.bdr}`, overflow:"hidden" }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 44px 44px 64px", padding:"9px 14px", background:C.card, borderBottom:`1px solid ${C.bdr}` }}>
          {["TEAM","W","L","PCT"].map((h, i) => (
            <span key={h} style={{ color:C.muted, fontSize:11, fontWeight:700, textAlign: i > 0 ? "center" : "left" }}>{h}</span>
          ))}
        </div>

        {sorted.map((t, i) => {
          const r = records[t.id] || { W:0, L:0 };
          return (
            <div key={t.id} style={{ display:"grid", gridTemplateColumns:"1fr 44px 44px 64px", padding:"13px 14px", borderBottom: i < sorted.length-1 ? `1px solid ${C.bdr}` : "none", alignItems:"center" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:3, height:34, borderRadius:2, background:t.color, flexShrink:0 }} />
                <div>
                  <p style={{ color:C.txt, fontSize:14, fontWeight:600, margin:0 }}>{t.name}</p>
                  <p style={{ color:C.muted, fontSize:11, margin:0 }}>{t.abbr}</p>
                </div>
                {i === 0 && (r.W + r.L > 0) && (
                  <span style={{ background:"#2d2a0e", color:C.ylw, fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:10 }}>1ST</span>
                )}
              </div>
              <span style={{ color:C.grn,  fontSize:16, fontWeight:700, textAlign:"center" }}>{r.W}</span>
              <span style={{ color:C.red,  fontSize:16, fontWeight:700, textAlign:"center" }}>{r.L}</span>
              <span style={{ color:C.muted, fontSize:14, textAlign:"center" }}>{pct(r)}</span>
            </div>
          );
        })}
      </div>

      <p style={{ color:C.muted, fontSize:12, textAlign:"center", marginTop:16 }}>
        {totalGames === 0 ? "No games played yet this season." : `${totalGames} game${totalGames !== 1 ? "s" : ""} played this season`}
      </p>

      {isAdmin && (
        <div style={{ marginTop:32, borderTop:`1px solid ${C.bdr}`, paddingTop:24 }}>
          <p style={{ color:C.muted, fontSize:11, fontWeight:700, letterSpacing:"1px", margin:"0 0 12px 2px" }}>
            SEASON MANAGEMENT
          </p>

          {resetDone && (
            <div style={{ background:"#0d2b1a", border:`1px solid ${C.grn}44`, borderRadius:10, padding:"12px 16px", marginBottom:12, textAlign:"center" }}>
              <p style={{ color:C.grn, fontWeight:700, fontSize:14, margin:0 }}>
                ✓ Season has been reset — all stats and standings cleared!
              </p>
            </div>
          )}

          {resetStep === "idle" && (
            <button onClick={() => setResetStep("password")}
              style={{ width:"100%", background:"transparent", color:C.red, border:`1px solid ${C.red}44`, borderRadius:10, padding:"13px", fontSize:14, fontWeight:700, cursor:"pointer" }}>
              🗑 Reset Season Data
            </button>
          )}

          {resetStep === "password" && (
            <div style={{ background:"#1a1a2a", border:`1px solid ${C.blue}44`, borderRadius:10, padding:"16px" }}>
              <p style={{ color:C.txt, fontSize:15, fontWeight:700, margin:"0 0 4px", textAlign:"center" }}>Enter Reset Password</p>
              <p style={{ color:C.muted, fontSize:13, margin:"0 0 14px", textAlign:"center" }}>
                This action requires a separate password to continue.
              </p>
              <input
                type="password"
                placeholder="Reset password"
                value={pwInput}
                onChange={e => { setPwInput(e.target.value); setPwError(""); }}
                onKeyDown={e => e.key === "Enter" && checkPassword()}
                style={{ background:C.card, border:`1px solid ${pwError ? C.red : C.bdr}`, borderRadius:8, padding:"12px 14px", color:C.txt, fontSize:15, outline:"none", width:"100%", boxSizing:"border-box", marginBottom:6 }}
              />
              {pwError && <p style={{ color:C.red, fontSize:12, margin:"0 0 10px" }}>{pwError}</p>}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginTop:10 }}>
                <button onClick={cancelReset}
                  style={{ background:C.card, color:C.txt, border:`1px solid ${C.bdr}`, borderRadius:8, padding:13, fontSize:14, fontWeight:700, cursor:"pointer" }}>
                  Cancel
                </button>
                <button onClick={checkPassword}
                  style={{ background:C.blue, color:"#fff", border:"none", borderRadius:8, padding:13, fontSize:14, fontWeight:700, cursor:"pointer" }}>
                  Continue
                </button>
              </div>
            </div>
          )}

          {resetStep === "confirm" && (
            <div style={{ background:"#2a0d0d", border:`1px solid ${C.red}66`, borderRadius:10, padding:"16px" }}>
              <p style={{ color:C.txt, fontSize:15, fontWeight:700, margin:"0 0 6px", textAlign:"center" }}>Are you sure?</p>
              <p style={{ color:C.muted, fontSize:13, margin:"0 0 16px", textAlign:"center" }}>
                This will permanently delete all standings and player stats. This cannot be undone.
              </p>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <button onClick={cancelReset}
                  style={{ background:C.card, color:C.txt, border:`1px solid ${C.bdr}`, borderRadius:8, padding:13, fontSize:14, fontWeight:700, cursor:"pointer" }}>
                  Cancel
                </button>
                <button onClick={resetSeason} disabled={resetting}
                  style={{ background:C.red, color:"#fff", border:"none", borderRadius:8, padding:13, fontSize:14, fontWeight:700, cursor:resetting?"default":"pointer" }}>
                  {resetting ? "Resetting..." : "Yes, Reset"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}